/**
 * The first name a signed-in seller never gave us, asked for where it shows.
 *
 * A phone or email signup is not required to give a name, and login's more-info
 * step that asks is dismissible — so accounts reach /sell with none, and their
 * listings go up under no name at all. One field, one save; no code to verify.
 */
import { useCallback, useState } from 'react';
import { useSession } from 'next-auth/react';
import { saveProfileName, type SaveNameError } from '../../utils/save-profile-name';

export type NameCaptureStage = 'name' | 'saved';

export interface UseNameCaptureOptions {
  /** Called once the name is on the account, so the page can refresh the profile. */
  onSaved?: (firstName: string) => void | Promise<void>;
}

export function useNameCapture(options: UseNameCaptureOptions = {}) {
  const { onSaved } = options;
  // The profile is seeded from the session, so writing the row is not enough:
  // until the session is refreshed every page still reads `firstName: null`.
  const { update: updateSession } = useSession();

  const [stage, setStage] = useState<NameCaptureStage>('name');
  const [name, setName] = useState('');
  const [savedFirstName, setSavedFirstName] = useState('');
  const [error, setError] = useState<SaveNameError | null>(null);
  const [busy, setBusy] = useState(false);

  const save = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const result = await saveProfileName(name);

    if (!result.success || !result.firstName) {
      setBusy(false);
      setError(result.error ?? 'save_failed');
      return false;
    }

    await updateSession();
    await onSaved?.(result.firstName);
    setSavedFirstName(result.firstName);
    setStage('saved');
    setBusy(false);
    return true;
  }, [name, onSaved, updateSession]);

  return {
    stage,
    name,
    setName,
    savedFirstName,
    error,
    busy,
    save,
    isSaved: stage === 'saved',
  };
}
